import { useState, useEffect, useCallback } from 'react';
import useJeMobil from '../hooks/useJeMobil';
import {
    Bug, Lightbulb, Plus, ChevronUp, MessageSquare, Paperclip,
    X, Trash2, Send, Monitor, Tag, Clock,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// ── Konfigurace typů a stavů ───────────────────────────────────
const TYP_CONFIG = {
    chyba: { label: 'Chyba', color: '#EF4444', bg: 'rgba(239,68,68,0.12)', Icon: Bug },
    napad: { label: 'Nápad', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', Icon: Lightbulb },
};

const STAV_CONFIG = {
    novy:      { label: 'Nový',      color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
    v_reseni:  { label: 'V řešení',  color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
    vyreseno:  { label: 'Vyřešeno',  color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
    zamitnuto: { label: 'Zamítnuto', color: '#6B7280', bg: 'rgba(107,114,128,0.12)' },
};

const PRIORITA_CONFIG = {
    nizka:   { label: 'Nízká',   color: '#6B7280' },
    stredni: { label: 'Střední', color: '#F59E0B' },
    vysoka:  { label: 'Vysoká',  color: '#EF4444' },
};

const STAVY = ['novy', 'v_reseni', 'vyreseno', 'zamitnuto'];
const PRIORITY = ['nizka', 'stredni', 'vysoka'];

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

function relativeTime(iso) {
    if (!iso) return '';
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1)  return 'právě teď';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24)   return `${h} h`;
    const d = Math.floor(h / 24);
    if (d < 30)   return `${d} d`;
    return `${Math.floor(d / 30)} měs.`;
}

function celeJmeno(autor) {
    if (!autor) return 'Neznámý';
    return `${autor.jmeno || ''} ${autor.prijmeni || ''}`.trim();
}

// ── Hlavní komponenta ──────────────────────────────────────────
export default function Podnety({ user }) {
    const isMobile = useJeMobil();
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const isAdmin = user && ['superadmin', 'admin'].includes(user.role);

    const [podnety, setPodnety]   = useState([]);
    const [loading, setLoading]   = useState(true);
    const [filtrTyp, setFiltrTyp] = useState('vse');
    const [filtrStav, setFiltrStav] = useState('vse');
    const [jenMoje, setJenMoje]   = useState(false);
    const [razeni, setRazeni]     = useState('hlasy');
    const [detailId, setDetailId] = useState(null);

    const fetchData = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({ razeni });
        if (filtrTyp !== 'vse')  params.set('typ', filtrTyp);
        if (filtrStav !== 'vse') params.set('stav', filtrStav);
        if (jenMoje) params.set('moje', '1');

        fetch(`/api/podnety?${params}`)
            .then(r => r.json())
            .then(data => { setPodnety(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, [filtrTyp, filtrStav, jenMoje, razeni]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Refresh po vytvoření podnětu přes plovoucí tlačítko
    useEffect(() => {
        const onCreated = () => fetchData();
        window.addEventListener('podnet-created', onCreated);
        return () => window.removeEventListener('podnet-created', onCreated);
    }, [fetchData]);

    const handleHlas = async (id, e) => {
        e.stopPropagation();
        const res = await fetch(`/api/podnety/${id}/hlas`, {
            method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() },
        }).then(r => r.json());
        setPodnety(prev => prev.map(p => p.id === id
            ? { ...p, muj_hlas: res.muj_hlas, pocet_hlasu: res.pocet_hlasu }
            : p));
    };

    return (
        <div>
            {/* Identita modulu jen na desktopu — na mobilu ji říká střed lišty.
                Tlačítko „Nový podnět" zůstává, to na liště není. */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                {!isMobile && (
                    <>
                        <div className="flex items-center justify-center shrink-0"
                            style={{ width: 40, height: 40, borderRadius: 7, background: 'rgba(180,122,234,0.12)' }}>
                            <Lightbulb size={20} style={{ color: '#B47AEA' }} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Podněty</h1>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                Nahlášené chyby a nápady na zlepšení systému
                            </p>
                        </div>
                    </>
                )}
                {isMobile && <span className="flex-1" />}
                <button
                    onClick={() => window.dispatchEvent(new Event('open-podnet'))}
                    className="flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-xl"
                    style={{ background: '#B47AEA', color: '#fff', border: 'none', cursor: 'pointer' }}
                >
                    <Plus size={16} /> Nový podnět
                </button>
            </div>

            {/* ── Filtry ── */}
            <div className="flex flex-wrap gap-2 mb-6 items-center">
                <Pill active={filtrTyp === 'vse'}   onClick={() => setFiltrTyp('vse')}   label="Vše"     isDark={isDark} />
                <Pill active={filtrTyp === 'chyba'} onClick={() => setFiltrTyp('chyba')} label="Chyby"   color="#EF4444" bg="rgba(239,68,68,0.12)" Icon={Bug} isDark={isDark} />
                <Pill active={filtrTyp === 'napad'} onClick={() => setFiltrTyp('napad')} label="Nápady"  color="#F59E0B" bg="rgba(245,158,11,0.12)" Icon={Lightbulb} isDark={isDark} />

                <span style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '0 4px' }} />

                <select
                    value={filtrStav}
                    onChange={e => setFiltrStav(e.target.value)}
                    className="text-xs px-2.5 py-1.5 rounded-lg"
                    style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', cursor: 'pointer' }}
                >
                    <option value="vse">Všechny stavy</option>
                    {STAVY.map(s => <option key={s} value={s}>{STAV_CONFIG[s].label}</option>)}
                </select>

                <select
                    value={razeni}
                    onChange={e => setRazeni(e.target.value)}
                    className="text-xs px-2.5 py-1.5 rounded-lg"
                    style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', cursor: 'pointer' }}
                >
                    <option value="hlasy">Nejvíce hlasů</option>
                    <option value="datum">Nejnovější</option>
                </select>

                <Pill active={jenMoje} onClick={() => setJenMoje(v => !v)} label="Moje podněty" isDark={isDark} />
            </div>

            {/* ── Seznam ── */}
            {loading ? (
                <div className="text-center py-16">
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Načítám…</p>
                </div>
            ) : podnety.length === 0 ? (
                <div className="text-center py-16">
                    <Lightbulb size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 12px', opacity: 0.4 }} />
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Žádné podněty</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {podnety.map(p => (
                        <PodnetCard
                            key={p.id} podnet={p} isDark={isDark}
                            onOpen={() => setDetailId(p.id)}
                            onHlas={e => handleHlas(p.id, e)}
                        />
                    ))}
                </div>
            )}

            {/* ── Detail ── */}
            {detailId && (
                <PodnetDetail
                    id={detailId} isDark={isDark} isAdmin={isAdmin} user={user}
                    onClose={() => setDetailId(null)}
                    onChanged={fetchData}
                />
            )}
        </div>
    );
}

// ── Filter pill ────────────────────────────────────────────────
function Pill({ active, onClick, label, color, bg, Icon, isDark }) {
    const c = color || 'var(--text-primary)';
    return (
        <button
            onClick={onClick}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg"
            style={{
                background: active ? (bg || 'var(--surface-elevated)') : (isDark ? 'rgba(255,255,255,0.04)' : 'transparent'),
                color: active ? c : 'var(--text-secondary)',
                border: `1px solid ${active && color ? color + '40' : 'var(--border-default)'}`,
                cursor: 'pointer', fontWeight: active ? 600 : 400, transition: 'all 0.15s ease',
            }}
        >
            {Icon && <Icon size={12} />}
            {label}
        </button>
    );
}

// ── Karta podnětu ──────────────────────────────────────────────
function PodnetCard({ podnet, isDark, onOpen, onHlas }) {
    const [hovered, setHovered] = useState(false);
    const typCfg = TYP_CONFIG[podnet.typ] || TYP_CONFIG.napad;
    const stavCfg = STAV_CONFIG[podnet.stav] || STAV_CONFIG.novy;
    const TypIcon = typCfg.Icon;
    const vyreseno = ['vyreseno', 'zamitnuto'].includes(podnet.stav);

    return (
        <div
            onClick={onOpen}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-raised)',
                border: `1px solid ${hovered ? typCfg.color + '55' : 'var(--border-default)'}`,
                borderLeft: `3px solid ${typCfg.color}`,
                borderRadius: 9, padding: 16, cursor: 'pointer',
                opacity: vyreseno ? 0.78 : 1, transition: 'all 0.15s ease',
                ...(isDark ? { backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' } : { boxShadow: 'var(--shadow-xs)' }),
            }}
        >
            {/* Badges */}
            <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md"
                    style={{ background: typCfg.bg, color: typCfg.color }}>
                    <TypIcon size={11} /> {typCfg.label}
                </span>
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-md"
                    style={{ background: stavCfg.bg, color: stavCfg.color }}>
                    {stavCfg.label}
                </span>
                {podnet.priorita && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md"
                        style={{ background: 'transparent', color: PRIORITA_CONFIG[podnet.priorita]?.color, border: `1px solid ${PRIORITA_CONFIG[podnet.priorita]?.color}40` }}>
                        {PRIORITA_CONFIG[podnet.priorita]?.label}
                    </span>
                )}
            </div>

            {/* Titulek */}
            <h3 className="text-sm font-semibold leading-tight mb-1 line-clamp-2" style={{ color: 'var(--text-primary)' }}>
                {podnet.titulek}
            </h3>
            <p className="text-xs leading-relaxed line-clamp-2 mb-3" style={{ color: 'var(--text-secondary)' }}>
                {podnet.popis}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2">
                <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {celeJmeno(podnet.autor)} · {relativeTime(podnet.created_at)}
                </span>
                <div className="flex items-center gap-2.5">
                    {podnet.pocet_komentaru > 0 && (
                        <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                            <MessageSquare size={12} /> {podnet.pocet_komentaru}
                        </span>
                    )}
                    {podnet.pocet_priloh > 0 && (
                        <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                            <Paperclip size={12} /> {podnet.pocet_priloh}
                        </span>
                    )}
                    <button
                        onClick={onHlas}
                        className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg"
                        style={{
                            background: podnet.muj_hlas ? 'rgba(180,122,234,0.15)' : (isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-elevated)'),
                            color: podnet.muj_hlas ? '#B47AEA' : 'var(--text-secondary)',
                            border: `1px solid ${podnet.muj_hlas ? 'rgba(180,122,234,0.4)' : 'var(--border-default)'}`,
                            cursor: 'pointer',
                        }}
                    >
                        <ChevronUp size={13} /> {podnet.pocet_hlasu}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Detail podnětu (modal) ─────────────────────────────────────
function PodnetDetail({ id, isDark, isAdmin, user, onClose, onChanged }) {
    const [podnet, setPodnet] = useState(null);
    const [novyKomentar, setNovyKomentar] = useState('');
    const [odesilam, setOdesilam] = useState(false);

    const nacti = useCallback(() => {
        fetch(`/api/podnety/${id}`).then(r => r.json()).then(setPodnet).catch(() => {});
    }, [id]);

    useEffect(() => { nacti(); }, [nacti]);

    const muzeMazat = podnet && (isAdmin || podnet.autor?.id === user?.id);

    const zmenStav = async (pole, hodnota) => {
        const res = await fetch(`/api/podnety/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify({ [pole]: hodnota || null }),
        });
        if (res.ok) { nacti(); onChanged?.(); }
    };

    const odeslatKomentar = async () => {
        if (!novyKomentar.trim()) return;
        setOdesilam(true);
        const res = await fetch(`/api/podnety/${id}/komentare`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify({ obsah: novyKomentar.trim() }),
        });
        setOdesilam(false);
        if (res.ok) { setNovyKomentar(''); nacti(); }
    };

    const smazat = async () => {
        if (!window.confirm('Opravdu smazat tento podnět?')) return;
        const res = await fetch(`/api/podnety/${id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
        if (res.ok) { onChanged?.(); onClose(); }
    };

    const typCfg = podnet ? (TYP_CONFIG[podnet.typ] || TYP_CONFIG.napad) : TYP_CONFIG.napad;
    const TypIcon = typCfg.Icon;

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 200,
                background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                padding: '4vh 16px', overflowY: 'auto',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: 640,
                    background: isDark ? 'rgba(28,28,38,0.98)' : 'var(--surface-raised)',
                    border: '1px solid var(--border-default)', borderRadius: 12,
                    overflow: 'hidden',
                }}
            >
                {!podnet ? (
                    <div className="text-center py-16"><p className="text-sm" style={{ color: 'var(--text-muted)' }}>Načítám…</p></div>
                ) : (
                    <>
                        {/* Hlavička */}
                        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border-default)' }}>
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md"
                                        style={{ background: typCfg.bg, color: typCfg.color }}>
                                        <TypIcon size={11} /> {typCfg.label}
                                    </span>
                                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md"
                                        style={{ background: STAV_CONFIG[podnet.stav]?.bg, color: STAV_CONFIG[podnet.stav]?.color }}>
                                        {STAV_CONFIG[podnet.stav]?.label}
                                    </span>
                                </div>
                                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                                    <X size={20} />
                                </button>
                            </div>
                            <h2 className="text-base font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{podnet.titulek}</h2>
                            <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                                {celeJmeno(podnet.autor)} · {relativeTime(podnet.created_at)}
                            </p>
                        </div>

                        <div style={{ padding: 20 }}>
                            {/* Popis */}
                            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                                {podnet.popis}
                            </p>

                            {/* Přílohy */}
                            {podnet.prilohy?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {podnet.prilohy.map(pr => (
                                        pr.je_obrazek ? (
                                            <a key={pr.id} href={pr.url} target="_blank" rel="noreferrer">
                                                <img src={pr.url} alt={pr.nazev_original}
                                                    style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border-default)' }} />
                                            </a>
                                        ) : (
                                            <a key={pr.id} href={pr.url} target="_blank" rel="noreferrer"
                                                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg"
                                                style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                                                <Paperclip size={13} /> {pr.nazev_original}
                                            </a>
                                        )
                                    ))}
                                </div>
                            )}

                            {/* Technický kontext */}
                            {(podnet.kontext_stranka || podnet.kontext_verze || podnet.kontext_prohlizec) && (
                                <div className="mb-4 text-[11px] rounded-lg p-3"
                                    style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-elevated)', color: 'var(--text-muted)' }}>
                                    <div className="flex items-center gap-1.5 font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                                        <Monitor size={12} /> Technický kontext
                                    </div>
                                    {podnet.kontext_stranka && <div>Stránka: <b>{podnet.kontext_stranka}</b></div>}
                                    {podnet.kontext_verze && <div>Verze aplikace: <b>{podnet.kontext_verze}</b></div>}
                                    {podnet.kontext_zarizeni && <div>Zařízení: <b>{podnet.kontext_zarizeni}</b></div>}
                                    {podnet.kontext_prohlizec && <div style={{ wordBreak: 'break-all' }}>Prohlížeč: {podnet.kontext_prohlizec}</div>}
                                    {podnet.kontext_url && <div style={{ wordBreak: 'break-all' }}>URL: {podnet.kontext_url}</div>}
                                </div>
                            )}

                            {/* Admin ovládání */}
                            {isAdmin && (
                                <div className="flex flex-wrap gap-2 mb-4 items-center">
                                    <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: 'var(--text-secondary)' }}>
                                        <Tag size={12} /> Správa:
                                    </span>
                                    <select value={podnet.stav} onChange={e => zmenStav('stav', e.target.value)}
                                        className="text-xs px-2.5 py-1.5 rounded-lg"
                                        style={{ background: 'var(--surface-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', cursor: 'pointer' }}>
                                        {STAVY.map(s => <option key={s} value={s}>{STAV_CONFIG[s].label}</option>)}
                                    </select>
                                    <select value={podnet.priorita || ''} onChange={e => zmenStav('priorita', e.target.value)}
                                        className="text-xs px-2.5 py-1.5 rounded-lg"
                                        style={{ background: 'var(--surface-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', cursor: 'pointer' }}>
                                        <option value="">Bez priority</option>
                                        {PRIORITY.map(p => <option key={p} value={p}>{PRIORITA_CONFIG[p].label}</option>)}
                                    </select>
                                </div>
                            )}

                            {/* Komentáře */}
                            <div className="flex items-center gap-1.5 text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                                <MessageSquare size={13} /> Diskuze ({podnet.komentare?.filter(k => !k.je_systemovy).length || 0})
                            </div>
                            <div className="flex flex-col gap-2.5 mb-4">
                                {podnet.komentare?.map(k => (
                                    k.je_systemovy ? (
                                        <div key={k.id} className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                                            <Clock size={11} /> {k.obsah} · {relativeTime(k.created_at)}
                                        </div>
                                    ) : (
                                        <div key={k.id} style={{
                                            background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-elevated)',
                                            borderRadius: 7, padding: '10px 12px',
                                        }}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{celeJmeno(k.autor)}</span>
                                                <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{relativeTime(k.created_at)}</span>
                                            </div>
                                            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{k.obsah}</p>
                                        </div>
                                    )
                                ))}
                            </div>

                            {/* Nový komentář */}
                            <div className="flex gap-2 items-end">
                                <textarea
                                    value={novyKomentar}
                                    onChange={e => setNovyKomentar(e.target.value)}
                                    placeholder="Napsat komentář…"
                                    rows={2}
                                    className="flex-1 text-sm px-3 py-2 rounded-lg resize-none"
                                    style={{ background: 'var(--surface-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }}
                                />
                                <button
                                    onClick={odeslatKomentar}
                                    disabled={odesilam || !novyKomentar.trim()}
                                    className="flex items-center justify-center rounded-lg shrink-0"
                                    style={{
                                        width: 38, height: 38, background: '#B47AEA', color: '#fff',
                                        border: 'none', cursor: 'pointer', opacity: (odesilam || !novyKomentar.trim()) ? 0.5 : 1,
                                    }}
                                >
                                    <Send size={16} />
                                </button>
                            </div>

                            {/* Smazat */}
                            {muzeMazat && (
                                <div className="flex justify-end mt-4">
                                    <button onClick={smazat}
                                        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg"
                                        style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer' }}>
                                        <Trash2 size={13} /> Smazat podnět
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
